import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Clock, Building2, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function RequestVerificationCard() {
  const [status, setStatus] = useState(null);
  const [organization, setOrganization] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/meetings/host-verification/me").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Tell us why you need Live Class hosting access"); return; }
    setSubmitting(true);
    try {
      await api.post("/meetings/host-verification/request", { organization, reason });
      toast.success("Verification request submitted — an admin will review it shortly.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (!status) return null;

  if (status.is_verified_host) {
    return (
      <div className="glass-panel rounded-2xl p-8 text-center" data-testid="host-verification-approved-card">
        <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-3" strokeWidth={1.5} />
        <h2 className="font-heading text-xl font-semibold text-white">You're a Verified Host</h2>
        <p className="text-sm text-slate-400 mt-2">You can create Live Class meetings from the Meetings page anytime.</p>
      </div>
    );
  }

  if (status.host_verification_status === "pending") {
    return (
      <div className="glass-panel rounded-2xl p-8 text-center" data-testid="host-verification-pending-card">
        <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" strokeWidth={1.5} />
        <h2 className="font-heading text-xl font-semibold text-white">Verification Pending</h2>
        <p className="text-sm text-slate-400 mt-2">An admin is reviewing your request to host Live Class meetings.</p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-2xl p-8 max-w-lg" data-testid="host-verification-request-card">
      <ShieldCheck className="w-8 h-8 text-cyan-400 mb-3" strokeWidth={1.5} />
      <h2 className="font-heading text-xl font-semibold text-white">Request Live Class Hosting Access</h2>
      <p className="text-sm text-slate-400 mt-2 mb-5">
        Live Class meetings (cohort classes & webinars) require host verification so students always know they're joining a legitimate session.
        {status.host_verification_status === "rejected" && " Your previous request was not approved — you can submit a new one below."}
      </p>
      <input
        value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="Organization / Team (optional)"
        data-testid="host-verification-organization-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3 placeholder:text-slate-500"
      />
      <textarea
        value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why do you need to host Live Classes?"
        rows={3} data-testid="host-verification-reason-input"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-4 placeholder:text-slate-500 resize-none"
      />
      <button
        onClick={submit} disabled={submitting} data-testid="host-verification-submit-btn"
        className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-background font-semibold px-5 py-2.5 rounded-xl transition-colors disabled:opacity-60"
      >
        <Send className="w-4 h-4" /> Submit Request
      </button>
    </div>
  );
}

function AdminReviewQueue() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/host-verifications").then((r) => setRequests(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (userId, action) => {
    try {
      await api.post(`/admin/host-verifications/${userId}/${action}`);
      toast.success(action === "approve" ? "Host verified" : "Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  const pending = requests.filter((r) => r.host_verification_status === "pending");
  const reviewed = requests.filter((r) => r.host_verification_status !== "pending");

  if (loading) return <p className="text-sm text-slate-500">Loading requests...</p>;

  return (
    <div className="space-y-8" data-testid="admin-host-verification-queue">
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-3">Pending ({pending.length})</h3>
        {pending.length === 0 && <p className="text-sm text-slate-500">No pending host verification requests.</p>}
        <div className="space-y-3">
          {pending.map((r) => (
            <div key={r.user_id} className="glass-panel rounded-xl p-5 flex items-start justify-between gap-4" data-testid={`host-verification-row-${r.user_id}`}>
              <div>
                <p className="font-semibold text-white text-sm">{r.name} <span className="text-slate-500 font-normal">· {r.email}</span></p>
                {r.host_verification_organization && (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> {r.host_verification_organization}</p>
                )}
                <p className="text-sm text-slate-300 mt-2">{r.host_verification_reason}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => act(r.user_id, "approve")} data-testid={`host-verification-approve-btn-${r.user_id}`} className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 px-3 py-2 rounded-lg">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button onClick={() => act(r.user_id, "reject")} data-testid={`host-verification-reject-btn-${r.user_id}`} className="flex items-center gap-1.5 text-xs font-semibold bg-red-500/20 text-red-300 hover:bg-red-500/30 px-3 py-2 rounded-lg">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {reviewed.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">Reviewed</h3>
          <div className="space-y-2">
            {reviewed.map((r) => (
              <div key={r.user_id} className="flex items-center justify-between gap-4 text-sm border border-white/10 rounded-lg px-4 py-2.5" data-testid={`host-verification-reviewed-row-${r.user_id}`}>
                <span className="text-slate-300">{r.name} <span className="text-slate-500">· {r.email}</span></span>
                <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${r.host_verification_status === "approved" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                  {r.host_verification_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminApprovalPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="max-w-4xl" data-testid="admin-approval-page">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="w-6 h-6 text-cyan-400" />
        <h1 className="font-heading text-2xl font-semibold text-white">Admin Approval</h1>
      </div>
      <p className="text-sm text-slate-400 mb-8 max-w-2xl">
        {isAdmin
          ? "Review requests from users who want to host Live Class meetings (cohort classes & webinars) on Cloudivo Meet."
          : "Live Class meetings need a verified host. Request access below."}
      </p>
      {isAdmin ? <AdminReviewQueue /> : <RequestVerificationCard />}
    </div>
  );
}
